import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import {
  RefreshCw,
  Download,
  ImageUp,
  CheckCircle2,
  AlertTriangle,
  MessageCircle,
  Copy,
  Link2,
} from 'lucide-react'
import apiClient from '@/api/client'
import { toastHelpers } from '@/lib/toast-helpers'

const CATALOG_STATUS_QK = ['admin', 'whatsapp-catalog', 'status'] as const

/**
 * WhatsApp Commerce catalog controls, surfaced as a tab on the Menu Management
 * page (the catalog IS the menu). Reports config/coverage, toggles auto-sync,
 * and exposes the one-off actions: push the menu to Meta, export the data-feed
 * CSV, and rehost product images to the CDN.
 */
export function AdminCatalogSyncTab() {
  const qc = useQueryClient()

  const statusQuery = useQuery({
    queryKey: CATALOG_STATUS_QK,
    queryFn: async () => (await apiClient.getCatalogStatus()).data,
  })
  const status = statusQuery.data
  const configured = !!status?.configured

  const syncMutation = useMutation({
    mutationFn: async () => (await apiClient.syncCatalogNow()).data,
    onSuccess: (d) => {
      toastHelpers.success(
        'Catalog synced',
        `Pushed ${d?.pushed ?? 0} item(s)${d?.skipped ? ` · ${d.skipped} skipped (no hosted image)` : ''}.`,
      )
      qc.invalidateQueries({ queryKey: CATALOG_STATUS_QK })
    },
    onError: (e: any) => toastHelpers.error('Sync failed', e?.message),
  })

  // Backfill runs as a loop of bounded server batches (each stays under the HTTP
  // timeout) so a big base64 menu can be hosted without a single long request.
  const [backfilling, setBackfilling] = useState(false)
  const runBackfill = async () => {
    setBackfilling(true)
    let migrated = 0
    let lastRemaining = 0
    try {
      for (let i = 0; i < 60; i++) {
        const d = (await apiClient.backfillProductImages()).data
        migrated += d?.migrated ?? 0
        lastRemaining = d?.remaining ?? 0
        qc.invalidateQueries({ queryKey: CATALOG_STATUS_QK })
        if (lastRemaining <= 0) break
        if ((d?.migrated ?? 0) === 0) break // no progress → the rest keep failing
      }
      if (lastRemaining > 0) {
        toastHelpers.error(
          'Some images not hosted',
          `Moved ${migrated}; ${lastRemaining} couldn’t be hosted — check those products’ images.`,
        )
      } else {
        toastHelpers.success('Images hosted', `Moved ${migrated} image(s) to the CDN.`)
      }
    } catch (e: any) {
      toastHelpers.error('Image backfill failed', e?.message)
    } finally {
      setBackfilling(false)
      qc.invalidateQueries({ queryKey: CATALOG_STATUS_QK })
      qc.invalidateQueries({ queryKey: ['admin-products'] })
    }
  }

  const toggleMutation = useMutation({
    mutationFn: async (on: boolean) =>
      (await apiClient.updateSetting('whatsapp_catalog_sync_enabled', on)).data,
    onSuccess: (_d, on) => {
      toastHelpers.success(on ? 'Auto-sync on' : 'Auto-sync off')
      qc.invalidateQueries({ queryKey: CATALOG_STATUS_QK })
    },
    onError: (e: any) => toastHelpers.error('Could not update setting', e?.message),
  })

  const exportCsv = async () => {
    try {
      await apiClient.exportCatalogCsv()
    } catch (e: any) {
      toastHelpers.error('CSV export failed', e?.message)
    }
  }

  const copyFeed = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url)
      toastHelpers.success('Copied', 'Feed URL copied to clipboard.')
    } catch {
      toastHelpers.error('Copy failed', 'Select and copy the URL manually.')
    }
  }

  const missing = status?.missing_image ?? 0

  return (
    <div className="space-y-6">
      {/* Intro */}
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-emerald-100 p-2 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
          <MessageCircle className="h-5 w-5" />
        </div>
        <div>
          <h3 className="text-lg font-semibold">WhatsApp Catalog</h3>
          <p className="text-sm text-muted-foreground">
            Publish this menu to your WhatsApp Commerce catalog so customers can browse and add
            items in WhatsApp. Item ids map to your products, so orders come straight into the
            online-orders inbox.
          </p>
        </div>
      </div>

      {/* Scheduled feed URL — the token-free automatic-sync path (works even for
          WhatsApp apps that can't get catalog_management). */}
      {status?.feed_url ? (
        <Card className="border-emerald-300 bg-emerald-50 dark:border-emerald-800/60 dark:bg-emerald-900/20">
          <CardContent className="space-y-2 pt-6 text-sm">
            <div className="flex items-center gap-2 font-medium text-emerald-900 dark:text-emerald-200">
              <Link2 className="h-4 w-4" /> Scheduled feed URL (recommended)
            </div>
            <p className="text-emerald-800 dark:text-emerald-300/90">
              In Commerce Manager → your catalog → <strong>Data Sources → Data Feed → Use a URL</strong>,
              paste this and set a schedule (e.g. hourly). Meta pulls your menu automatically — no
              token or catalog permission needed.
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate rounded bg-white/70 px-2 py-1 text-xs dark:bg-black/30">
                {status.feed_url}
              </code>
              <Button size="sm" variant="outline" onClick={() => copyFeed(status.feed_url)}>
                <Copy className="mr-1 h-3 w-3" /> Copy
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        !statusQuery.isLoading && (
          <Card className="border-dashed">
            <CardContent className="pt-6 text-sm text-muted-foreground">
              <span className="font-medium">Scheduled feed URL</span> — set{' '}
              <code>CATALOG_FEED_TOKEN</code> and <code>APP_URL</code> on the backend to enable the
              token-free auto-sync feed (Meta pulls the CSV on a schedule; no catalog permission
              needed).
            </CardContent>
          </Card>
        )
      )}

      {/* Not-configured guidance (API items_batch path) */}
      {!statusQuery.isLoading && !configured && (
        <Card className="border-amber-300 bg-amber-50 dark:border-amber-800/60 dark:bg-amber-900/20">
          <CardContent className="flex items-start gap-3 pt-6 text-sm">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
            <div className="space-y-1">
              <p className="font-medium text-amber-900 dark:text-amber-200">
                Catalog sync isn’t configured yet
              </p>
              <p className="text-amber-800 dark:text-amber-300/90">
                Set <code>META_CATALOG_ID</code> and <code>META_CATALOG_ACCESS_TOKEN</code> on this
                tenant’s backend (a system-user token with <code>catalog_management</code>). Full
                steps are in <code>docs/WHATSAPP_CATALOG_SETUP_2026-07-12.md</code>. You can still
                export a CSV to upload manually below.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Status */}
      <Card>
        <CardContent className="grid grid-cols-2 gap-4 pt-6 sm:grid-cols-4">
          <Stat label="Sync configured">
            {configured ? (
              <Badge className="bg-emerald-600">
                <CheckCircle2 className="mr-1 h-3 w-3" /> Yes
              </Badge>
            ) : (
              <Badge variant="secondary">No</Badge>
            )}
          </Stat>
          <Stat label="Available items">{status?.available_products ?? '—'}</Stat>
          <Stat label="Catalog-ready">{status?.catalog_ready ?? '—'}</Stat>
          <Stat label="Missing image">
            <span className={missing > 0 ? 'text-amber-600 dark:text-amber-400' : ''}>
              {status?.missing_image ?? '—'}
            </span>
          </Stat>
        </CardContent>
      </Card>

      {/* Auto-sync toggle */}
      <Card>
        <CardContent className="flex items-center justify-between pt-6">
          <div>
            <p className="font-medium">Auto-sync on menu edits</p>
            <p className="text-sm text-muted-foreground">
              Push changes to the catalog automatically when you add, edit, price, 86, or delete an
              item.
            </p>
          </div>
          <Switch
            checked={!!status?.auto_sync_enabled}
            disabled={!configured || toggleMutation.isPending}
            onCheckedChange={(v) => toggleMutation.mutate(v)}
          />
        </CardContent>
      </Card>

      {/* Missing-image nudge */}
      {missing > 0 && (
        <p className="text-sm text-amber-700 dark:text-amber-400">
          {missing} item(s) have no hosted image and won’t appear in the catalog. Run{' '}
          <strong>Backfill images</strong> to fix that.
        </p>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <Button onClick={() => syncMutation.mutate()} disabled={!configured || syncMutation.isPending}>
          <RefreshCw className={`mr-2 h-4 w-4 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
          Sync now
        </Button>
        <Button variant="outline" onClick={exportCsv}>
          <Download className="mr-2 h-4 w-4" />
          Download CSV
        </Button>
        <Button variant="outline" onClick={runBackfill} disabled={backfilling}>
          <ImageUp className={`mr-2 h-4 w-4 ${backfilling ? 'animate-pulse' : ''}`} />
          {backfilling ? 'Hosting images…' : 'Backfill images'}
        </Button>
      </div>
    </div>
  )
}

function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="mt-1 text-lg font-semibold">{children}</div>
    </div>
  )
}
